import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { usePermissions } from "../../utilis/PermissionsContext";
import {
  isSidebarSectionVisible,
  isSidebarSubItemVisible,
} from "../../utilis/sidebarPermissionHelpers";
import styled from "styled-components";
import ReactTooltip from "react-tooltip";

const SidebarLink = styled(Link)`
  display: flex;
  color: rgba(255, 255, 255, 0.92);
  justify-content: space-between;
  align-items: center;
  padding: 8px 8px 8px 11px;
  list-style: none;
  min-height: 36px;
  text-decoration: none;
  font-size: 12.5px;
  font-family: "Segoe UI", -apple-system, Roboto, sans-serif;
  font-weight: 400;
  letter-spacing: 0.01em;
  transition:
    background 180ms ease,
    color 180ms ease,
    box-shadow 180ms ease;
  border-radius: 7px;
  position: relative;
  margin: 1px 0;

  &:hover {
    background: rgba(255, 255, 255, 0.07);
    color: #ffffff;
    text-decoration: none;
    cursor: pointer;
  }

  &.active {
    background: #1870e6;
    color: #fff;
    font-weight: 500;
    box-shadow: 0 2px 10px rgba(24, 112, 230, 0.3);
  }

  &.active::before {
    content: "";
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 60%;
    background: rgba(255, 255, 255, 0.85);
    border-radius: 0 3px 3px 0;
  }
`;

const SidebarLabel = styled.span`
  margin-left: 9px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  line-height: 1.3;
`;

const DropdownLink = styled(Link)`
  min-height: 32px;
  width: 100%;
  padding: 6px 8px 6px 12px;
  display: flex;
  align-items: center;
  text-decoration: none;
  color: rgba(255, 255, 255, 0.88);
  font-size: 12px;
  font-family: "Segoe UI", -apple-system, Roboto, sans-serif;
  letter-spacing: 0.01em;
  transition: background 180ms ease, color 180ms ease;
  border-radius: 6px;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    text-decoration: none;
    color: #ffffff;
    cursor: pointer;
  }

  &.active {
    background: rgba(24, 112, 230, 0.45);
    color: #ffffff;
    font-weight: 500;
  }
`;

const FlyoutDropdownLink = styled(Link)`
  min-height: 36px;
  width: 100%;
  padding: 8px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: rgba(255, 255, 255, 0.92);
  font-size: 12.5px;
  font-family: "Segoe UI", -apple-system, Roboto, sans-serif;
  letter-spacing: 0.01em;
  transition: background 150ms ease, color 150ms ease;

  &:hover {
    background: rgba(24, 112, 230, 0.28);
    color: #fff;
    text-decoration: none;
  }

  &.active {
    background: #1870e6;
    color: #fff;
    font-weight: 500;
  }
`;

const SubMenu = ({
  item,
  sidebarClose,
  isOpen = false,
  onToggle,
  onCloseAll,
}) => {
  const { permissions } = usePermissions();
  void permissions;
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [flyoutPos, setFlyoutPos] = useState({ top: 0, left: 0, maxHeight: 0 });
  const itemRef = useRef(null);
  const flyoutInnerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const location = useLocation();

  const updateFlyoutPosition = useCallback(() => {
    if (!itemRef.current) return;

    const margin = 12;
    const rect = itemRef.current.getBoundingClientRect();
    const maxHeight = window.innerHeight - margin * 2;
    let top = rect.top;

    if (flyoutInnerRef.current) {
      const flyoutHeight = Math.min(
        flyoutInnerRef.current.getBoundingClientRect().height,
        maxHeight,
      );

      if (top + flyoutHeight > window.innerHeight - margin) {
        top = Math.max(margin, window.innerHeight - margin - flyoutHeight);
      }
    } else if (top + maxHeight > window.innerHeight - margin) {
      top = Math.max(margin, window.innerHeight - margin - maxHeight);
    }

    if (top < margin) top = margin;

    setFlyoutPos({
      top,
      left: rect.right + 6,
      maxHeight,
    });
  }, []);

  const openFlyout = useCallback(() => {
    if (!sidebarClose || !item.subNav) return;
    clearTimeout(closeTimerRef.current);
    updateFlyoutPosition();
    setFlyoutOpen(true);
  }, [sidebarClose, item.subNav, updateFlyoutPosition]);

  const scheduleCloseFlyout = useCallback(() => {
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setFlyoutOpen(false), 150);
  }, []);

  const cancelCloseFlyout = useCallback(() => {
    clearTimeout(closeTimerRef.current);
  }, []);

  useEffect(() => {
    if (!flyoutOpen) return undefined;

    const handleReposition = () => updateFlyoutPosition();
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);

    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [flyoutOpen, updateFlyoutPosition]);

  useEffect(
    () => () => {
      clearTimeout(closeTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (!sidebarClose) {
      setFlyoutOpen(false);
    }
  }, [sidebarClose]);

  useLayoutEffect(() => {
    if (!flyoutOpen) return;
    updateFlyoutPosition();
  }, [flyoutOpen, updateFlyoutPosition]);

  const subNavPaths = (item.subNav || []).map((sub) => sub.path).filter(Boolean);

  const isPathActive = (path) => {
    if (!path) return false;
    const current = location.pathname.replace(/\/$/, "") || "/";
    const target = path.replace(/\/$/, "") || "/";

    const matches = (candidate) =>
      current === candidate || current.startsWith(`${candidate}/`);

    if (!matches(target)) return false;

    if (current === target) return true;

    // Shared prefixes (e.g. /company-forms vs /company-forms/manage): only the longest match is active.
    const siblingMatches = subNavPaths
      .map((p) => p.replace(/\/$/, "") || "/")
      .filter((candidate) => matches(candidate));

    if (siblingMatches.length === 0) return true;

    const bestMatch = siblingMatches.reduce((longest, candidate) =>
      candidate.length > longest.length ? candidate : longest,
    );

    return target === bestMatch;
  };

  const isParentActive =
    isPathActive(item.path) ||
    (item.subNav && item.subNav.some((sub) => isPathActive(sub.path)));

  const renderSubNavItems = (linkComponent) => {
    const LinkComp = linkComponent;
    return item.subNav.map((subItem, index) => {
      if (!isSidebarSubItemVisible(subItem)) {
        return null;
      }

      return (
        <LinkComp
          key={index}
          to={subItem.path}
          className={isPathActive(subItem.path) ? "active" : ""}
          onClick={() => {
            onCloseAll?.();
            setFlyoutOpen(false);
          }}
        >
          {subItem.icon}
          <SidebarLabel>{subItem.title}</SidebarLabel>
        </LinkComp>
      );
    });
  };

  if (!isSidebarSectionVisible(item.title)) {
    return null;
  }

  return (
    <div
      ref={itemRef}
      className={
        sidebarClose ? "sidebar-item-collapsed" : "sidebar-item-expanded"
      }
      onMouseEnter={openFlyout}
      onMouseLeave={scheduleCloseFlyout}
    >
      <SidebarLink
        to={item.path}
        className={`${sidebarClose ? "sidebar-link-collapsed" : ""} ${
          isParentActive ? "active" : ""
        }`}
        onClick={(e) => {
          if (item.subNav) {
            e.preventDefault();
            if (!sidebarClose) {
              onToggle?.();
            }
          } else {
            onCloseAll?.();
          }
        }}
      >
        <div
          className={`sidebar-link-inner ${
            sidebarClose ? "sidebar-icon-collapsed" : ""
          }`}
          data-tip={sidebarClose && !item.subNav ? item.title : undefined}
          data-for={sidebarClose && !item.subNav ? item.title : undefined}
        >
          {item.icon}
          {!sidebarClose && <SidebarLabel>{item.title}</SidebarLabel>}
        </div>

        {sidebarClose && !item.subNav && (
          <ReactTooltip
            id={item.title}
            backgroundColor="#0f2d6e"
            textColor="rgba(255,255,255,0.95)"
            place="right"
            effect="solid"
            className="sidebar-tooltip"
            offset={{ right: 8 }}
          >
            {item.title}
          </ReactTooltip>
        )}

        {!sidebarClose && (
          <div className={`sidebar-chevron ${isOpen ? "sidebar-chevron-open" : ""}`}>
            {item.subNav && isOpen
              ? item.iconOpened
              : item.subNav
                ? item.iconClosed
                : null}
          </div>
        )}
      </SidebarLink>

      {isOpen && !sidebarClose && item.subNav && (
        <div className="sub-menu">
          {renderSubNavItems(DropdownLink)}
        </div>
      )}

      {sidebarClose &&
        item.subNav &&
        flyoutOpen &&
        createPortal(
          <div
            className="sidebar-flyout-portal"
            style={{
              top: flyoutPos.top,
              left: flyoutPos.left,
            }}
            onMouseEnter={cancelCloseFlyout}
            onMouseLeave={scheduleCloseFlyout}
          >
            <div
              ref={flyoutInnerRef}
              className="sidebar-flyout-inner"
              style={{ maxHeight: flyoutPos.maxHeight || "calc(100vh - 24px)" }}
            >
              <div className="sidebar-flyout-header">{item.title}</div>
              <div className="sidebar-flyout-items">
                {renderSubNavItems(FlyoutDropdownLink)}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default SubMenu;
